import { useEffect, useState, useCallback, useRef } from "react";
import moment from "moment";
import { BigBoldText } from "../dashboard/home/home.style";
import {
  DynamicTitle,
  Title,
} from "../../../components/DashboardComps/Dashboard.styles";
import {
  Link,
  Route,
  useLocation,
  Routes,
  useSearchParams,
} from "react-router-dom";
import List, { INCIDENT_GRID_KEY_PREFIX } from "./List";
import { useStoreWrapper, useViewStateWrapper } from "../../../store/store";
import ActionBtnU from "../Users/components/ActionBtnU";
import { IoMdArrowRoundBack } from "react-icons/io";
import { CustomDatePicker, FacilitySelect } from "../analytics/form/incident-inputs";
import { Input, InputChangeEvent } from "@progress/kendo-react-inputs";

const Incident = ({ title }: any) => {
  const [searchParams] = useSearchParams();
  // Deep links (dashboard widgets) carry their own filters, so they neither restore nor save a view.
  const hasUrlFilters = [
    "incidentType",
    "facilityName",
    "startDate",
    "endDate",
  ].some((param) => searchParams.get(param));
  const location = useLocation();
  // View saved when the user last left this screen (e.g. to open an incident).
  const [restoredView] = useState(() => {
    const viewState = useViewStateWrapper.getState();
    // Dashboard entries start fresh unless the user is coming back from an incident.
    const isDashboard = location.pathname.includes("dashboard");
    const returning = viewState.leavingForIncident;
    if (!returning && (isDashboard || hasUrlFilters)) {
      viewState.clearGridViewsWithPrefix(INCIDENT_GRID_KEY_PREFIX);
      viewState.setIncidentListTab(null);
    }
    return hasUrlFilters || (isDashboard && !returning)
      ? null
      : viewState.incidentListView;
  });
  useEffect(() => {
    useViewStateWrapper.getState().setLeavingForIncident(false);
  }, []);
  const [incidentTypeParam, setIncidentTypeParam] = useState<any>("");
  const [facilityNameParam, setFacilityNameParam] = useState<any>(
    restoredView?.facility && restoredView.facility.value !== "all_facilities"
      ? restoredView.facility.label
      : ""
  );
  const [allTabInitial, setAllTabInitial] = useState<any>(
    Boolean(restoredView?.searchInput)
  );
  const [resetFilters, setResetFilters] = useState<any>(false);
  const [startDateParam, setStartDateParam] = useState<any>(
    restoredView?.startDate ?? moment().add(-1, "years").format("l")
  );
  const [endDateParam, setEndDateParam] = useState<any>(
    restoredView?.endDate ?? moment().format("l")
  );
  const [dynamicTitle, setDynamicTitle] = useState<any>("");


  const { userFacilities } = useStoreWrapper((state) => ({
    userFacilities: state.userFacilities,
  }));
  const [facilityOptions, setFacilityOptions] = useState<any>([]);
  const [selectedFacility, setSelectedFacility] = useState<any>(
    restoredView?.facility ?? []
  );
  const [searchValue, setSearchValue] = useState(
    restoredView?.searchInput ?? ""
  );
  const [inputValue, setInputValue] = useState(
    restoredView?.searchInput ?? ""
  );
  // Only reset a date when its URL param was just removed (not on mount, StrictMode-safe).
  const urlHadStart = useRef(false);
  const urlHadEnd = useRef(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedSetSearchValue = useCallback((value: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setSearchValue(value);
    }, 500);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const incidentType = searchParams.get("incidentType");
    const facilityName = searchParams.get("facilityName");
    const startDate = searchParams.get("startDate");
    const endDate = searchParams.get("endDate");
    if (incidentType) {
      setIncidentTypeParam(incidentType);
    } else {
      setIncidentTypeParam("");
    }
    if (facilityName) {
      setFacilityNameParam(facilityName);  
    } else {
      setFacilityNameParam("");
    }
    if (startDate) {
      setStartDateParam(startDate);
    } else if (urlHadStart.current) {
      setStartDateParam(moment().add(-1, "years").format("l"));
    }
    if (endDate) {
      setEndDateParam(endDate);
    } else if (urlHadEnd.current) {
      setEndDateParam(moment().format("l"));
    }
    urlHadStart.current = !!startDate;
    urlHadEnd.current = !!endDate;
  }, [searchParams]);

  useEffect(() => {
    if (hasUrlFilters) return;
    useViewStateWrapper.getState().setIncidentListView({
      startDate: startDateParam,
      endDate: endDateParam,
      facility:
        Array.isArray(selectedFacility) || !selectedFacility
          ? null
          : selectedFacility,
      searchInput: inputValue,
    });
  }, [startDateParam, endDateParam, selectedFacility, inputValue]);

  useEffect(() => {
    if (incidentTypeParam.length > 0) {
      setDynamicTitle(`/${incidentTypeParam}`);
      setAllTabInitial(true);
    }
  }, [incidentTypeParam, facilityNameParam]);

  useEffect(() => {
    if (Array.isArray(userFacilities) && userFacilities.length > 0) {
      const facilityName = searchParams.get("facilityName");
      if (facilityName && facilityName.length > 0) {
        setAllTabInitial(true);
        const title = userFacilities.filter(
          (fac: any) => facilityName === fac.facilityId
        );
        if (title.length > 0)
        {
          setDynamicTitle(`/${title[0].facilityName}`);
          const allFacilities = [
            { label: title[0].facilityName, value: facilityName }];
          setFacilityOptions(allFacilities);
          setSelectedFacility(allFacilities[0]);
        }       
      } else {
        // Memoize facility transformation to avoid expensive operations
        const facilityMap = new Map();
        userFacilities.forEach((facility: any) => {
          facilityMap.set(facility.facilityId, {
            label: facility.facilityName,
            value: facility.facilityId,
          });
        });
        
        const allFacilities = [
          { label: "All Facilities", value: "all_facilities" },
          ...Array.from(facilityMap.values()),
        ];    
        setFacilityOptions(allFacilities);    
      }      
    }
  }, [userFacilities, searchParams]);

  useEffect(() => {
    if (selectedFacility && selectedFacility.value !== "all_facilities") {
      setFacilityNameParam(selectedFacility.label)
    } else {  
      setFacilityNameParam("")
    }
  }, [selectedFacility]);

  const handleSearchChange = (event: InputChangeEvent) => {
    if (allTabInitial !== true) setAllTabInitial(true)
    setInputValue(event.value);
    debouncedSetSearchValue(event.value);
  }

  const handleClearFilters = () => {
    setStartDateParam(moment().add(-1, "years").format("l"));
    setEndDateParam(moment().format("l"));
    setSelectedFacility([]);
    setFacilityNameParam("");
    setSearchValue("");
    setInputValue("");
    useViewStateWrapper
      .getState()
      .clearGridViewsWithPrefix(INCIDENT_GRID_KEY_PREFIX);
    setResetFilters(true);
  };

  useEffect(() => {
    if (resetFilters) {
      // Reset immediately since we're now using initialFilter array changes
      setResetFilters(false);
    }
  }, [resetFilters]);
  return (
    <div title="incidents">
      <div className="mb-10 mt-10">
        {location.pathname.includes("dashboard") && (
          <>
            <ActionBtnU
              borderStyle="1px solid rgb(181, 128, 255)"
              color="rgb(181, 128, 255)"
              onClick={() => window.history.back()}
            >
              <span className="mr-2 mt-1 font-bold">
                <IoMdArrowRoundBack />
              </span>
              Back
            </ActionBtnU>
            <Title>
              <Link to="/user/dashboard">Dashboard</Link>
              <DynamicTitle>/{title} </DynamicTitle>
              <DynamicTitle>{dynamicTitle}</DynamicTitle>
            </Title>
          </>
        )}
        {!location.pathname.includes("dashboard") && (
          <BigBoldText data-testid="incidents-title">
            <span className="text-2xl">Incidents</span>
          </BigBoldText>
        )}
      </div>
      <div style={{ display: "flex", gap: "20px", marginBottom: "20px",  flexWrap: "wrap" }}>
        <div style={{ flex: 1 }}>
          <span className="text-medium font-semibold">Start Date</span>
          <CustomDatePicker
            handleSetStartDates={setStartDateParam}
            endDate={endDateParam}
            startDate={startDateParam}
            isStartDate={true}
          />
        </div>
        <div style={{ flex: 1 }}>
          <span className="text-medium font-semibold">End Date</span>
          <CustomDatePicker
            handleSetEndDates={setEndDateParam}
            endDate={endDateParam}
            startDate={startDateParam}
            isStartDate={false}
          />
        </div>
        <div style={{ flex: 1 }}>
          <FacilitySelect
            name="facilityInfo"
            placeholder="Select Facility"
            options={facilityOptions}
            isMulti={false}
            onChange={(opt) => setSelectedFacility(opt)}
            selected={selectedFacility}
          />
        </div>
        <div style={{ flex: 1 }}>
          <span className="text-medium font-semibold">Advanced Search</span>
          <Input
            id="advanced-search"
            onChange={handleSearchChange}
            value={inputValue}
            style={{ minWidth: 180 }}
            placeholder="Search Incidents..."
            className="mt-1"
          />
        </div>
        <div style={{ flex: 1, marginTop: "8px"}}>
          <ActionBtnU
              purpleBg
              white_color
              height="50px"
              onClick={() => handleClearFilters()}
            >
              Clear All Filters
            </ActionBtnU>
        </div>
      </div>
      {/* <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
        The default date range, if none is selected, is the last 30 days.
      </div> */}
      <Routes>
        <Route
          path="/"
          element={
        <List
          startDateParam={startDateParam}
          endDateParam={endDateParam}
          incidentTypeParam={incidentTypeParam}
          facilityNameParam={facilityNameParam}
          allTabInitial={allTabInitial}
          searchValue={searchValue}
          resetFilters={resetFilters}
        />
          }
        />
      </Routes>
    </div>
  );
};

export default Incident;
